<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPrint, faTimes, faFileInvoiceDollar } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'

library.add(faPrint, faTimes, faFileInvoiceDollar)

const SLOTS_PER_PAGE = 4
const SLOT_TEXT_CHARS_PER_LINE = 24
const SLOT_LINE_CAPACITY = 38
const ORDER_ROW_BUFFER_LINES = 1

type PrintableInvoiceSlot = {
    invoice: any
    orders: any[]
    partNumber: number
    totalParts: number
    orderStartIndex: number
}

const props = defineProps<{
    invoices: any[]
}>()

const isOpen = ref(false)
const printTime = ref('')

const openPreview = () => {
    const now = new Date()
    const weekday = now.toLocaleDateString('id-ID', { weekday: 'long' })
    const date = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
    const time = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
    printTime.value = `${weekday}, ${date} ${time} WIB`
    isOpen.value = true
}

const estimateTextLines = (text: unknown, charsPerLine = SLOT_TEXT_CHARS_PER_LINE): number => {
    const value = String(text ?? '').trim()

    if (!value) return 0

    return value
        .split(/\r?\n/)
        .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / charsPerLine)), 0)
}

const estimateHeaderLines = (invoice: any): number => {
    return [
        1, // logo + print time
        Math.max(1, estimateTextLines(invoice.reference)),
        1, // created at
        invoice.customer_name ? estimateTextLines(`Pelanggan: ${invoice.customer_name}`) : 0,
        invoice.sales?.name ? estimateTextLines(`Kasir: ${invoice.sales.name}`) : 0,
        1, // table header
    ].reduce((total, lineCount) => total + lineCount, 0)
}

const estimateFooterLines = (invoice: any): number => {
    return [
        1, // total
        (invoice.additional_costs?.length ?? 0), // one line per additional cost
        invoice.amount_remaining > 0 && invoice.amount_paid > 0 ? 1 : 0,
        invoice.amount_remaining > 0 ? 1 : 0,
    ].reduce((total, lineCount) => total + lineCount, 0)
}

const estimateOrderLines = (order: any): number => {
    return [
        Math.max(1, estimateTextLines(order.product_name)),
        estimateTextLines(order.reference),
        estimateTextLines(order.file_merek),
        ORDER_ROW_BUFFER_LINES,
    ].reduce((total, lineCount) => total + lineCount, 0)
}

const splitInvoiceIntoSlots = (invoice: any): PrintableInvoiceSlot[] => {
    const availableOrderLines = Math.max(
        1,
        SLOT_LINE_CAPACITY - estimateHeaderLines(invoice) - estimateFooterLines(invoice),
    )

    const orders = invoice.orders ?? []

    if (orders.length === 0) {
        return [{
            invoice,
            orders: [],
            partNumber: 1,
            totalParts: 1,
            orderStartIndex: 0,
        }]
    }

    const chunks: { orders: any[], orderStartIndex: number }[] = []
    let currentOrders: any[] = []
    let currentLines = 0
    let currentOrderStartIndex = 0

    for (let index = 0; index < orders.length; index += 1) {
        const order = orders[index]
        const orderLines = estimateOrderLines(order)
        const shouldStartNewSlot = currentOrders.length > 0 && (currentLines + orderLines) > availableOrderLines

        if (shouldStartNewSlot) {
            chunks.push({
                orders: currentOrders,
                orderStartIndex: currentOrderStartIndex,
            })

            currentOrders = []
            currentLines = 0
            currentOrderStartIndex = index
        }

        currentOrders.push(order)
        currentLines += orderLines
    }

    if (currentOrders.length > 0) {
        chunks.push({
            orders: currentOrders,
            orderStartIndex: currentOrderStartIndex,
        })
    }

    return chunks.map((chunk, index) => ({
        invoice,
        orders: chunk.orders,
        partNumber: index + 1,
        totalParts: chunks.length,
        orderStartIndex: chunk.orderStartIndex,
    }))
}

const printableInvoiceSlots = computed<PrintableInvoiceSlot[]>(() => {
    const result: PrintableInvoiceSlot[] = []

    for (const invoice of props.invoices) {
        result.push(...splitInvoiceIntoSlots(invoice))
    }

    return result
})

const pages = computed<PrintableInvoiceSlot[][]>(() => {
    const result: PrintableInvoiceSlot[][] = []
    let currentPage: PrintableInvoiceSlot[] = []

    for (const slot of printableInvoiceSlots.value) {
        if (currentPage.length === SLOTS_PER_PAGE) {
            result.push(currentPage)
            currentPage = [slot]
        } else {
            currentPage.push(slot)
        }
    }

    if (currentPage.length > 0) {
        result.push(currentPage)
    }

    return result
})

const closePreview = () => { isOpen.value = false }

const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) closePreview()
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

const doPrint = () => {
    const now = new Date()
    const date = now.toLocaleDateString('sv-SE')
    const time = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false })
    const filename = `Invoices - ${date} ${time} - ${props.invoices.length}`

    const originalTitle = document.title
    document.title = filename
    window.print()
    document.title = originalTitle
}
</script>

<template>
    <div>
        <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border"
            :class="invoices.length > 0
                ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'"
            :disabled="invoices.length === 0"
            @click="openPreview"
        >
            <FontAwesomeIcon icon="fal fa-print" fixed-width aria-hidden="true" />
            Cetak PDF
            <span
                class="ml-0.5 text-xs rounded-full px-1.5 leading-tight"
                :class="invoices.length > 0 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500'"
            >
                {{ invoices.length }}
            </span>
        </button>

        <Teleport to="body">
            <div v-if="isOpen" class="print-preview-overlay">
                <div class="print-toolbar no-print">
                    <div class="flex items-center gap-3">
                        <FontAwesomeIcon icon="fal fa-file-invoice-dollar" class="text-indigo-600 text-lg" />
                        <div>
                            <div class="font-semibold text-gray-800 text-sm leading-tight">Preview Cetak Invoice</div>
                            <div class="text-xs text-gray-500">
                                {{ invoices.length }} invoice dipilih &bull; {{ pages.length }} halaman
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            @click="doPrint"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700"
                        >
                            <FontAwesomeIcon icon="fal fa-print" />
                            Cetak / Simpan PDF
                        </button>
                        <button
                            @click="closePreview"
                            class="inline-flex items-center gap-1.5 px-3 py-2 text-gray-600 text-sm rounded hover:bg-gray-200"
                        >
                            <FontAwesomeIcon icon="fal fa-times" />
                            Tutup
                            <kbd class="ml-1 px-1 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded text-gray-500">Esc</kbd>
                        </button>
                    </div>
                </div>

                <div id="print-invoices-area" class="print-pages-wrapper">
                    <div
                        v-for="(page, pageIndex) in pages"
                        :key="pageIndex"
                        class="invoice-print-page"
                    >
                        <div class="page-invoice-grid">
                            <div
                                v-for="slot in page"
                                :key="`${slot.invoice.id}-${slot.partNumber}-${slot.orderStartIndex}`"
                                class="invoice-print-slot"
                            >
                                <div class="slot-header">
                                    <div class="slot-logo-row">
                                        <img src="/logo-kr-transparent.webp" class="slot-logo" alt="Logo" />
                                        <div class="slot-print-time">Diprint: {{ printTime }}</div>
                                    </div>
                                    <div class="flex justify-between items-start gap-2">
                                        <div class="min-w-0">
                                            <div class="slot-reference-row">
                                                <div class="slot-reference">{{ slot.invoice.reference }} <span v-if="slot.totalParts > 1" class="slot-part-badge">
                                                    {{ slot.partNumber }}/{{ slot.totalParts }}
                                                </span></div>
                                                
                                            </div>
                                            <div class="slot-meta">{{ useFormatTime(slot.invoice.created_at) }}</div>
                                        </div>
                                        <div v-if="slot.invoice.billing_date" class="slot-paid-badge">LUNAS</div>
                                    </div>
                                    <div v-if="slot.invoice.customer_name" class="slot-meta">
                                        Pelanggan: <span class="font-medium text-gray-700">{{ slot.invoice.customer_name }}</span>
                                    </div>
                                    <div v-if="slot.invoice.sales" class="slot-meta">
                                        Kasir: <span class="font-medium text-gray-700">{{ slot.invoice.sales.name }}</span>
                                    </div>
                                </div>

                                <div class="slot-orders">
                                    <table class="slot-orders-table">
                                        <thead>
                                            <tr>
                                                <th class="w-7"></th>
                                                <th class="text-left">Produk</th>
                                                <th class="text-right w-8">Qty</th>
                                                <th class="text-right">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(order, idx) in slot.orders" :key="order.id">
                                                <td class="text-center tabular-nums">
                                                    {{ slot.orderStartIndex + idx + 1 }}.
                                                </td>
                                                <td class="slot-product-cell">
                                                    <div class="slot-product-name">{{ order.product_name }}</div>
                                                    <div class="slot-product-ref">{{ order.reference }}</div>
                                                    <div v-if="order.file_merek" class="slot-product-note">
                                                        {{ order.file_merek }}
                                                    </div>
                                                </td>
                                                <td class="text-right">{{ order.quantity_in_invoice ?? order.quantity }}</td>
                                                <td class="text-right whitespace-nowrap tabular-nums">{{ useFormatCurrency(order.total_amount_in_invoice ?? order.total_amount) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div class="slot-footer">
                                    <div
                                        v-for="cost in slot.invoice.additional_costs"
                                        :key="cost.id"
                                        class="flex justify-between items-center mb-0.5"
                                    >
                                        <span class="slot-cost-label">{{ cost.description }}</span>
                                        <span class="slot-cost-value">+{{ useFormatCurrency(cost.amount) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center">
                                        <span class="slot-total-label">Total Tagihan</span>
                                        <span class="slot-total-value">{{ useFormatCurrency(slot.invoice.final_total_transfer) }}</span>
                                    </div>
                                    <div v-if="slot.invoice.amount_remaining > 0 && slot.invoice.amount_paid > 0" class="flex justify-between items-center mt-0.5">
                                        <span class="slot-sisa-label">Sudah Dibayar</span>
                                        <span class="slot-sisa-paid">{{ useFormatCurrency(slot.invoice.amount_paid) }}</span>
                                    </div>
                                    <div v-if="slot.invoice.amount_remaining > 0" class="flex justify-between items-center">
                                        <span class="slot-sisa-label">Sisa Tagihan</span>
                                        <span class="slot-sisa-value">{{ useFormatCurrency(slot.invoice.amount_remaining) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="no-print page-number-indicator">
                            Halaman {{ pageIndex + 1 }} dari {{ pages.length }}
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style>
.print-preview-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: #d1d5db;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.print-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
    width: 100%;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
}

.print-pages-wrapper {
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
}

.invoice-print-page {
    width: 210mm;
    min-height: 297mm;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, .15);
    padding: 10mm;
    box-sizing: border-box;
}

.page-invoice-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 5mm;
    height: calc(297mm - 20mm);
}

.invoice-print-slot {
    border: 1px solid #d1d5db;
    border-radius: 2mm;
    padding: 4mm;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    min-width: 0;
    min-height: 0;
}

.slot-header {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 2mm;
    margin-bottom: 2mm;
    flex-shrink: 0;
}

.slot-reference-row {
    display: flex;
    align-items: center;
    gap: 1.5mm;
    min-width: 0;
}

.slot-reference {
    display: flex;
    align-items: center;
    gap: 1.5mm;
    font-size: 10pt;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    min-width: 0;
}

.slot-part-badge {
    flex-shrink: 0;
    font-size: 6.5pt;
    font-weight: 700;
    color: #4338ca;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    border-radius: 999px;
    padding: 0.3mm 1.5mm;
    line-height: 1.2;
}

.slot-meta {
    font-size: 7pt;
    color: #6b7280;
    line-height: 1.4;
}

.slot-paid-badge {
    font-size: 6pt;
    font-weight: 700;
    color: #16a34a;
    border: 1px solid #16a34a;
    padding: 0.5mm 2mm;
    border-radius: 1mm;
    white-space: nowrap;
    flex-shrink: 0;
}

.slot-orders {
    flex: 1;
    overflow: hidden;
    min-height: 0;
}

.slot-orders-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 7pt;
    table-layout: fixed;
}

.slot-orders-table thead tr {
    border-bottom: 1px solid #d1d5db;
}

.slot-orders-table th {
    font-weight: 600;
    color: #4b5563;
    padding: 0.5mm 0;
    font-size: 6.5pt;
}

.slot-orders-table td {
    padding: 0.5mm 0;
    color: #1f2937;
    border-bottom: 1px solid #f3f4f6;
    line-height: 1.3;
    vertical-align: top;
}

.slot-product-cell {
    min-width: 0;
}

.slot-product-name,
.slot-product-ref,
.slot-product-note {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.slot-product-name {
    font-weight: 700;
}

.slot-product-ref,
.slot-product-note {
    font-size: 6.5pt;
    color: #6b7280;
}

.slot-product-note {
    font-style: italic;
}

.slot-footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 2mm;
    margin-top: 2mm;
    flex-shrink: 0;
}

.slot-cost-label {
    font-size: 7pt;
    color: #6b7280;
}

.slot-cost-value {
    font-size: 7pt;
    color: #6b7280;
}

.slot-total-label {
    font-size: 7.5pt;
    color: #6b7280;
}

.slot-total-value {
    font-size: 9.5pt;
    font-weight: 700;
    color: #111827;
}

.page-number-indicator {
    text-align: center;
    font-size: 11px;
    color: #9ca3af;
    margin-top: 8px;
}

.slot-logo-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5mm;
    padding-bottom: 1.5mm;
    border-bottom: 1px dashed #e5e7eb;
}

.slot-logo {
    height: 6mm;
    width: auto;
    object-fit: contain;
}

.slot-print-time {
    font-size: 5.5pt;
    color: #9ca3af;
    text-align: right;
    line-height: 1.3;
}

.slot-sisa-label {
    font-size: 7pt;
    color: #9ca3af;
}

.slot-sisa-paid {
    font-size: 7.5pt;
    font-weight: 600;
    color: #16a34a;
}

.slot-sisa-value {
    font-size: 7.5pt;
    font-weight: 600;
    color: #dc2626;
}

@media print {
    @page {
        size: A4;
        margin: 0;
    }

    body > * {
        visibility: hidden;
    }

    .print-preview-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        overflow: visible !important;
        height: auto !important;
        background: none !important;
        display: block !important;
        width: 100% !important;
    }

    #print-invoices-area,
    #print-invoices-area * {
        visibility: visible;
    }

    #print-invoices-area {
        position: static !important;
        padding: 0;
        gap: 0;
        display: block;
    }

    .no-print {
        display: none !important;
        visibility: hidden !important;
    }

    .invoice-print-page {
        margin: 0;
        box-shadow: none;
        page-break-after: always;
        break-after: page;
        width: 210mm;
        min-height: 0;
        height: auto;
    }

    .invoice-print-page:last-child {
        page-break-after: avoid;
        break-after: avoid;
    }

    .page-invoice-grid {
        height: 253mm;
    }
}
</style>
